import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Scale, Newspaper, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import YearsSeal from "@/components/YearsSeal";
import { NEWSPAPER_LOGOS, SAMPLE_NOTICE_IMAGES } from "@/lib/brand";

const PAPERS = [
  { name: "Lokmat", lang: "MR" },
  { name: "Lokmat Times", lang: "EN" },
  { name: "Lokmat Samachar", lang: "HI" },
  { name: "Sakal", lang: "MR" },
  { name: "Divya Marathi", lang: "MR" },
  { name: "Maharashtra Times", lang: "MR" },
  { name: "Times of India", lang: "EN" },
  { name: "Punya Nagari", lang: "MR" },
  { name: "Saamana", lang: "MR" },
];

function PaperLogo({ name, lang }) {
  const logo = NEWSPAPER_LOGOS[name];
  const slug = name.toLowerCase().replace(/\s+/g, "-");
  const langLabel = lang === "MR" ? "Marathi Daily" : lang === "HI" ? "Hindi Daily" : "English Daily";
  if (logo) {
    return (
      <div
        className="shrink-0 w-40 sm:w-48 h-20 sm:h-24 bg-white border border-black/15 rounded-sm flex items-center justify-center p-3 mx-1.5 sm:mx-2"
        data-testid={`media-logo-${slug}`}
        title={name}
      >
        <img src={logo} alt={name} className="max-h-full max-w-full object-contain" loading="lazy" />
      </div>
    );
  }
  return (
    <div
      className="shrink-0 w-40 sm:w-48 h-20 sm:h-24 bg-white border border-black/15 rounded-sm flex flex-col items-center justify-center px-3 mx-1.5 sm:mx-2"
      data-testid={`media-logo-${slug}`}
    >
      <div className="font-display text-base sm:text-lg font-bold text-black leading-none text-center">{name}</div>
      <div className="text-[9px] tracking-overline uppercase mt-1 text-gray-500 font-semibold">{langLabel}</div>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Sticky floating "Start Booking" CTA — bottom CENTER, mobile-safe */}
      <Link
        to="/book"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold px-5 sm:px-6 py-3 sm:py-3.5 rounded-full shadow-card-hover transition active:scale-95 text-sm sm:text-base"
        data-testid="sticky-cta-book"
      >
        <Newspaper size={18}/> <span>{t("home.cta_band_button")}</span>
      </Link>

      {/* HERO */}
      <section className="relative bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 lg:pt-20 pb-16 sm:pb-20 lg:pb-24 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-black/20 text-black text-[10px] tracking-overline uppercase font-semibold rounded-full mb-5 sm:mb-7"
                 data-testid="trust-badge-60years">
              {t("home.years_badge")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.0] font-bold text-black tracking-tight">
              {t("home.hero_title_line1")}
              <span className="block text-gray-500 font-normal italic">{t("home.hero_title_line2")}</span>
            </h1>
            <p className="mt-5 sm:mt-7 text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl leading-relaxed">
              {t("home.hero_subtitle")}
            </p>
            <div className="mt-7 sm:mt-9 flex flex-wrap items-center gap-4">
              <Link to="/book"
                    className="group inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold px-5 sm:px-6 py-3 sm:py-3.5 rounded-sm transition"
                    data-testid="hero-cta-book">
                {t("home.cta_primary")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/pricing"
                    className="inline-flex items-center gap-2 border border-black text-black hover:bg-black hover:text-white transition px-4 sm:px-5 py-3 sm:py-3.5 rounded-sm text-sm sm:text-base font-semibold"
                    data-testid="hero-cta-pricing">
                See all rates
              </Link>
              <Link to="/about"
                    className="inline-flex items-center gap-2 text-gray-700 hover:text-black transition px-2 py-2 sm:py-3 text-sm sm:text-base"
                    data-testid="hero-cta-about">
                {t("home.cta_secondary")} →
              </Link>
            </div>
            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-x-6 sm:gap-x-8 gap-y-3 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-black"/> {t("home.trust_secure")}</div>
              <div className="flex items-center gap-2"><Scale size={16} className="text-black"/> {t("home.trust_legal")}</div>
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-black"/> {t("home.trust_discount")}</div>
            </div>
          </div>

          {/* Right: seal + 2 sample notice images (compact, side by side) */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-end gap-5 animate-fade-up">
            <div data-testid="years-seal">
              <YearsSeal size={120} />
            </div>
            <div className="bg-white p-3 rounded-sm border border-black/15 w-full max-w-md" data-testid="sample-notice-card">
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold mb-2 px-1 pt-0.5">{t("home.samples_label")}</div>
              <div className="grid grid-cols-3 gap-2">
                {SAMPLE_NOTICE_IMAGES.map((img) => {
                  const cap = img.label === "English" ? t("home.sample_english")
                    : img.label === "Marathi" ? t("home.sample_marathi")
                    : "Change of Name";
                  return (
                    <figure key={img.label} className="flex flex-col items-center" data-testid={`sample-${img.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      <img src={img.src} alt={img.alt}
                           className="w-full h-auto rounded-sm border border-black/15"
                           style={{ maxHeight: 220, objectFit: "contain" }}
                           loading="lazy" />
                      <figcaption className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold mt-1 text-center leading-tight">
                        {cap}
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
              <div className="text-[10px] text-gray-500 mt-2 px-1 italic text-center">{t("home.sample_footnote")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Process at top */}
      <section className="py-12 sm:py-16 bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">{t("home.process_overline")}</div>
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl text-black mt-2 mb-8 font-bold tracking-tight">{t("home.process_title")}</h2>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { n: "01", tKey: "step1" },
              { n: "02", tKey: "step2" },
              { n: "03", tKey: "step3" },
            ].map((s) => (
              <div key={s.n} className="bg-white p-6 sm:p-7 border border-black/15 rounded-sm hover:border-black transition" data-testid={`process-step-${s.n}`}>
                <div className="font-mono text-gray-400 font-bold text-sm">{s.n}</div>
                <h3 className="font-display text-xl sm:text-2xl mt-3 mb-2 text-black font-bold">{t(`home.${s.tKey}_title`)}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{t(`home.${s.tKey}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services strip */}
      <section className="py-12 sm:py-16 bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">What we do</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-black mt-2 mb-8 font-bold tracking-tight">A full-service ad bureau — newspapers, radio, outdoor &amp; beyond.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
            {[
              "Newspaper Advertisement",
              "Bank Notice Publication",
              "Radio Advertising",
              "Outdoor & Hoarding Ads",
              "Media Planning",
              "Branding & Marketing",
              "Rural & Urban Campaigns",
              "Pan-India Print Network",
            ].map((s) => (
              <div key={s} className="px-4 py-3 border border-black/15 rounded-sm bg-white text-black">
                <span className="text-gray-400 font-mono mr-2">·</span>{s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEDIA ASSOCIATES — RTL marquee */}
      <section className="py-14 sm:py-20 bg-gray-50 border-b border-black/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 mb-8 sm:mb-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div>
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Our Network</div>
              <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl text-black mt-2 font-bold tracking-tight">Media Associates</h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 max-w-xs">Leading Marathi, English &amp; Hindi dailies — pan-India circulation reaching crores of readers daily.</p>
          </div>
        </div>

        <div className="relative" data-testid="media-marquee">
          <div className="flex w-max animate-marquee" style={{ animationDuration: "45s" }}>
            {[...PAPERS, ...PAPERS].map((p, i) => (
              <PaperLogo key={`${p.name}-${i}`} name={p.name} lang={p.lang} />
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-gray-50 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-gray-50 to-transparent" />
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-black py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 text-center text-white">
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-4 sm:mb-5 font-bold tracking-tight">{t("home.cta_band_title")}</h3>
          <p className="text-white/70 mb-7 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-base">{t("home.cta_band_subtitle")}</p>
          <Link to="/book"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-200 text-black font-semibold px-6 sm:px-7 py-3 sm:py-3.5 rounded-sm transition"
                data-testid="cta-band-book">
            {t("home.cta_band_button")} <ArrowRight size={18}/>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
